import { OccupancyType } from "../types/stateroom";
import { CrewType, CrewTypeDefinition } from "../types/crew";

// Weapon type definition
export interface WeaponType {
  id: string;
  name: string;
  cost: number; // MCr
  weight: number; // tons
  power: number;
  crew: number;
  damage: string;
  range: string;
  techLevel: number;
  traits: string[];
  description: string;
  mountType: string;
  ammo?: {
    type: string;
    capacity: number;
    tonsPerReload: number;
    costPerReload: number;
  };
  attackModifier?: number;
  damageMultiple?: number;
  baseSize?: number;
  maxSize?: number;
  intercept?: number;
}

// Power Plant Types
export const POWER_PLANT_TYPES = {
  STANDARD: {
    name: "Standard Power Plant",
    description: "Basic power generation system",
    baseCost: 50000,
    baseTons: 0.1,
    basePower: 10,
    techLevel: 7,
    fuelEfficiency: 1.0,
  },
  ADVANCED: {
    name: "Advanced Power Plant",
    description: "Improved efficiency power system",
    baseCost: 75000,
    baseTons: 0.12,
    basePower: 15,
    techLevel: 8,
    fuelEfficiency: 1.2,
  },
  MILITARY: {
    name: "Military Power Plant",
    description: "High output, specialized power system",
    baseCost: 100000,
    baseTons: 0.15,
    basePower: 20,
    techLevel: 9,
    fuelEfficiency: 1.5,
  },
  FUSION: {
    name: "Fusion Power Plant",
    description: "Maximum power, advanced technology",
    baseCost: 150000,
    baseTons: 0.2,
    basePower: 25,
    techLevel: 10,
    fuelEfficiency: 2.0,
  },
} as const;

// Stateroom Costs and Weights
export const STATEROOM_CONFIG = {
  single: {
    name: "Single Stateroom",
    cost: 500000,
    tons: 4,
    space: 4,
    description: "Private accommodation for one person",
    techLevel: 7,
    power: 0,
    maintenanceCost: 1000,
  },
  double: {
    name: "Double Stateroom",
    cost: 500000,
    tons: 4,
    space: 4,
    description: "Shared accommodation for two people (same cost as single stateroom)",
    techLevel: 7,
    power: 0,
    maintenanceCost: 1000,
  },
} as const;

export const STATEROOM_TYPES: Record<
  OccupancyType,
  {
    id: OccupancyType;
    name: string;
    cost: number;
    weight: number;
    power: number;
    crew: number;
    description: string;
    techLevel: number;
    maintenanceCost: number;
  }
> = {
  single: {
    id: "single",
    name: "Single Stateroom",
    cost: 0.5, // MCr
    weight: 4, // tons
    power: 0,
    crew: 0,
    description: "Standard single occupancy stateroom",
    techLevel: 7,
    maintenanceCost: 1000,
  },
  double: {
    id: "double",
    name: "Double Stateroom",
    cost: 0.5, // MCr (same as single stateroom)
    weight: 4, // tons
    power: 0,
    crew: 0,
    description: "Standard double occupancy stateroom (same cost as single)",
    techLevel: 7,
    maintenanceCost: 1000,
  },
} as const;

// Crew Costs
export const CREW_COSTS = {
  monthly: 5000, // Cost per crew member per month
  lifeSupport: 1000, // Cost per person per month for life support
} as const;

// Maintenance Costs
export const MAINTENANCE_COSTS = {
  powerPlant: 0.01, // 1% of power plant cost per month
  drives: 0.01, // 1% of drive cost per month
} as const;

// Base Costs
export const BASE_COSTS = {
  powerPlant: 100000, // Base cost per ton for power plant
  drive: 50000, // Base cost per ton for drives
  hull: 100000, // Base cost per ton for hull
} as const;

// Base Power Requirements
export const BASE_POWER = {
  basicSystems: 1, // Base power for basic ship systems
  bridge: 1, // Base power for bridge
  sensors: 1, // Base power for sensors
} as const;

// Validation Constants
export const VALIDATION = {
  minShipSize: 5, // Traveller rules: minimum 5 tons
  minJumpCapableShipSize: 100, // Traveller rules: jump-capable ships must be at least 100 tons
  minPowerPlantRating: 1,
  minDriveRating: 1,
  minTechLevel: 7,
  maxShipSize: 1000000,
  maxTechLevel: 20,
} as const;

export const ARMOR_TYPES = {
  TITANIUM_STEEL: {
    name: "Titanium Steel",
    baseCost: 50000,
    baseTons: 0.025, // 2.5%
    techLevel: 7,
    protection: "TL or 9 (whichever is less)",
    special: null,
  },
  CRYSTALIRON: {
    name: "Crystaliron",
    baseCost: 200000,
    baseTons: 0.0125, // 1.25%
    techLevel: 10,
    protection: "TL or 13 (whichever is less)",
    special: null,
  },
  BONDED_SUPERDENSE: {
    name: "Bonded Superdense",
    baseCost: 500000,
    baseTons: 0.008, // 0.80%
    techLevel: 14,
    protection: "TL",
    special: null,
  },
  MOLECULAR_BONDED: {
    name: "Molecular Bonded",
    baseCost: 1500000, // MCr1.5
    baseTons: 0.005, // 0.50%
    techLevel: 16,
    protection: "TL+4",
    special: null,
  },
} as const;

// Bridge size ranges in tons - REMOVED: Now defined in bridges.ts
// Bridge types - REMOVED: Now defined in bridges.ts

export const SENSOR_TYPES = {
  BASIC: {
    name: "Basic Sensor Suite",
    baseCost: 50000,
    baseTons: 1,
    basePower: 1,
    minTechLevel: 7,
    range: "Close",
    resolution: "Standard",
  },
  MILITARY: {
    name: "Military Sensors",
    baseCost: 100000,
    baseTons: 2,
    basePower: 2,
    minTechLevel: 8,
    range: "Medium",
    resolution: "High",
  },
  ADVANCED: {
    name: "Advanced Sensors",
    baseCost: 200000,
    baseTons: 3,
    basePower: 3,
    minTechLevel: 9,
    range: "Long",
    resolution: "Very High",
  },
  SCIENTIFIC: {
    name: "Scientific Sensors",
    baseCost: 300000,
    baseTons: 4,
    basePower: 4,
    minTechLevel: 10,
    range: "Very Long",
    resolution: "Extreme",
  },
};

export const HULL_TYPES = {
  STANDARD: {
    name: "Standard Hull",
    baseCost: 50000,
    baseTons: 0.1,
    minSize: 100,
    maxSize: 1000000,
    hullPoints: 1,
    maintenanceCost: 0.1,
  },
  STREAMLINED: {
    name: "Streamlined Hull",
    baseCost: 75000,
    baseTons: 0.15,
    minSize: 100,
    maxSize: 1000000,
    hullPoints: 1,
    maintenanceCost: 0.15,
    special: "Atmospheric Capable",
  },
  DISTRIBUTED: {
    name: "Distributed Hull",
    baseCost: 100000,
    baseTons: 0.2,
    minSize: 1000,
    maxSize: 1000000,
    hullPoints: 2,
    maintenanceCost: 0.2,
    special: "Enhanced Protection",
  },
  PLANETOID: {
    name: "Planetoid Hull",
    baseCost: 150000,
    baseTons: 0.3,
    minSize: 10000,
    maxSize: 1000000,
    hullPoints: 3,
    maintenanceCost: 0.25,
    special: "Maximum Protection",
  },
} as const;

// Software Types
export const SOFTWARE_TYPES = {
  NAVIGATION: {
    name: "Navigation Software",
    description: "Basic navigation and course plotting",
    baseCost: 10000,
    baseTons: 0,
    techLevel: 7,
    rating: 1,
  },
  TARGETING: {
    name: "Targeting Software",
    description: "Weapon targeting and fire control",
    baseCost: 15000,
    baseTons: 0,
    techLevel: 8,
    rating: 1,
  },
  ENGINEERING: {
    name: "Engineering Software",
    description: "Power plant and drive optimization",
    baseCost: 20000,
    baseTons: 0,
    techLevel: 8,
    rating: 1,
  },
  LIFE_SUPPORT: {
    name: "Life Support Software",
    description: "Environmental and life support control",
    baseCost: 12000,
    baseTons: 0,
    techLevel: 7,
    rating: 1,
  },
  SENSOR: {
    name: "Sensor Software",
    description: "Sensor data processing and analysis",
    baseCost: 18000,
    baseTons: 0,
    techLevel: 8,
    rating: 1,
  },
  COMMS: {
    name: "Communications Software",
    description: "Communication system management",
    baseCost: 10000,
    baseTons: 0,
    techLevel: 7,
    rating: 1,
  },
} as const;

export const SHIP_BUILDER_STEPS = [
  { id: "initial", label: "Step 0: Ship Size and Technology Level" },
  {
    id: "hull-and-armor",
    label: "Step 1: Hull Configuration & Install Armour",
  },
  { id: "drives", label: "Step 2: Install Drives" },
  { id: "power", label: "Step 3: Install Power Plant" },
  { id: "fuel", label: "Step 4: Install Fuel Tanks" },
  { id: "bridge", label: "Step 5: Install Bridge" },
  { id: "computer", label: "Step 6: Install Computer" },
  { id: "sensors", label: "Step 7: Install Sensors" },
  { id: "weapons", label: "Step 8: Install Weapons" },
  { id: "optional-systems", label: "Step 9: Install Optional Systems" },
  { id: "crew", label: "Step 10: Determine Crew" },
  { id: "staterooms", label: "Step 11: Install Staterooms" },
  { id: "cargo", label: "Step 12: Cargo Configuration" },
  { id: "summary", label: "Step 13: Finalise Design" },
] as const;

export type ShipBuilderStepId = (typeof SHIP_BUILDER_STEPS)[number]["id"];

/**
 * Get ship builder steps
 * @returns Array of ship builder steps
 */
export const getShipBuilderSteps = () => {
  return SHIP_BUILDER_STEPS;
};

export const CARGO_TYPES = [
  {
    id: "standard",
    name: "Standard Cargo Hold",
    cost: 0.05, // MCr per ton
    weight: 1, // tons per ton of cargo
    power: 0,
    crew: 0,
    description: "Standard cargo hold for general purpose storage",
  },
] as const;

export const FUEL_TYPES = [
  {
    id: "standard",
    name: "Standard Fuel Tank",
    cost: 0.05, // MCr per ton
    weight: 1, // tons per ton of fuel
    power: 0,
    crew: 0,
    description: "Standard fuel tank for storing refined fuel",
  },
] as const;

export const WEAPON_TYPES: Record<string, WeaponType> = {
  // Small Weapons (≤250 kg) - 0.25 tons each, no hardpoints
  machineGun: {
    id: "machineGun",
    name: "Machine Gun",
    cost: 0.005, // MCr0.005 (Cr5,000)
    weight: 0.25, // 0.25 tons (≤250 kg)
    power: 0,
    crew: 0, // Can be automated
    damage: "1D",
    range: "Short",
    techLevel: 6,
    traits: ["Small Weapon"] as string[],
    description: "Small automatic weapon for ground targets. 0.25 tons per weapon, no hardpoints required.",
    mountType: "smallWeaponMount",
    ammo: {
      type: "machineGunAmmo",
      capacity: 100,
      tonsPerReload: 0.1,
      costPerReload: 1000, // Cr1,000 for 100 rounds
    },
  },
  autocannon: {
    id: "autocannon",
    name: "Autocannon",
    cost: 0.01, // MCr0.01 (Cr10,000)
    weight: 0.25, // 0.25 tons (≤250 kg)
    power: 0,
    crew: 0, // Can be automated
    damage: "2D",
    range: "Short",
    techLevel: 7,
    traits: ["Small Weapon"] as string[],
    description: "Heavy automatic weapon for ground targets. 0.25 tons per weapon, no hardpoints required.",
    mountType: "smallWeaponMount",
    ammo: {
      type: "autocannonAmmo",
      capacity: 50,
      tonsPerReload: 0.1,
      costPerReload: 2000, // Cr2,000 for 50 rounds
    },
  },
  grenadeLauncher: {
    id: "grenadeLauncher",
    name: "Grenade Launcher",
    cost: 0.008, // MCr0.008 (Cr8,000)
    weight: 0.25, // 0.25 tons (≤250 kg)
    power: 0,
    crew: 0, // Can be automated
    damage: "3D",
    range: "Short",
    techLevel: 7,
    traits: ["Small Weapon", "Explosive"] as string[],
    description: "Explosive weapon for ground targets. 0.25 tons per weapon, no hardpoints required.",
    mountType: "smallWeaponMount",
    ammo: {
      type: "grenadeAmmo",
      capacity: 20,
      tonsPerReload: 0.1,
      costPerReload: 1500, // Cr1,500 for 20 grenades
    },
  },
  
  // Turret Weapons
  pulseLaser: {
    id: "pulseLaser",
    name: "Pulse Laser",
    cost: 1, // MCr (corrected from Traveller rules)
    weight: 1, // tons
    power: 4, // Traveller rules: 4 power
    crew: 1,
    damage: "2D",
    range: "Long", // Traveller rules: Long range
    techLevel: 9, // Traveller rules: TL 9
    traits: [] as string[],
    description: "Pulse laser turrets receive DM+2 to attack rolls",
    mountType: "turret",
    attackModifier: 2,
  },
  beamLaser: {
    id: "beamLaser",
    name: "Beam Laser",
    cost: 0.5, // MCr (corrected from Traveller rules)
    weight: 1, // tons (corrected from Traveller rules)
    power: 4,
    crew: 1,
    damage: "1D",
    range: "Medium",
    techLevel: 10,
    traits: [] as string[],
    description: "Beam laser turrets receive DM+4 to attack rolls",
    mountType: "turret",
    attackModifier: 4,
  },
  missileRack: {
    id: "missileRack",
    name: "Missile Rack",
    cost: 0.75, // MCr (corrected from Traveller rules)
    weight: 1, // tons (corrected from Traveller rules)
    power: 0, // Traveller rules: 0 power
    crew: 1,
    damage: "4D",
    range: "Special", // Traveller rules: Special range
    techLevel: 7,
    traits: ["Smart"] as string[],
    description: "Each turret with one or more missile racks holds 12 missiles (4 on firmpoints)",
    mountType: "turret",
    ammo: {
      type: "missile",
      capacity: 12,
      tonsPerReload: 1,
      costPerReload: 250000, // Cr250,000 for 12 standard missiles
    },
  },
  sandcaster: {
    id: "sandcaster",
    name: "Sandcaster",
    cost: 0.25, // MCr (corrected from rules)
    weight: 1, // tons
    power: 0,
    crew: 1,
    damage: "Special",
    range: "Special",
    techLevel: 9,
    traits: ["Point Defense"] as string[],
    description: "Defensive sandcaster weapon",
    mountType: "turret",
    ammo: {
      type: "sand",
      capacity: 20,
      tonsPerReload: 1,
      costPerReload: 25000,
    },
  },
  railgun: {
    id: "railgun",
    name: "Railgun",
    cost: 1, // MCr
    weight: 1, // tons
    power: 2,
    crew: 1,
    damage: "2D",
    range: "Short",
    techLevel: 10,
    traits: ["AP 4"] as string[],
    description: "Electromagnetic railgun with 12 rounds",
    mountType: "turret",
    ammo: {
      type: "railgunAmmo",
      capacity: 12,
      tonsPerReload: 1,
      costPerReload: 5000,
    },
  },
  fusionGun: {
    id: "fusionGun",
    name: "Fusion Gun",
    cost: 2, // MCr
    weight: 2, // tons
    power: 12,
    crew: 1,
    damage: "4D",
    range: "Medium",
    techLevel: 14,
    traits: ["Radiation"] as string[],
    description: "Powerful fusion-based weapon",
    mountType: "turret",
  },
  laserDrill: {
    id: "laserDrill",
    name: "Laser Drill",
    cost: 0.15, // MCr (Cr150,000)
    weight: 1, // tons
    power: 4,
    crew: 1,
    damage: "2D",
    range: "Adjacent",
    techLevel: 8,
    traits: ["AP 4"] as string[],
    description:
      "Industrial laser drill, DM-3 to attack rolls when used as weapon",
    mountType: "turret",
    attackModifier: -3,
  },
  particleBeam: {
    id: "particleBeam",
    name: "Particle Beam",
    cost: 4, // MCr
    weight: 2, // tons
    power: 8,
    crew: 1,
    damage: "3D",
    range: "Very Long",
    techLevel: 12,
    traits: ["Radiation"] as string[],
    description: "Particle beam weapon with armor penetration",
    mountType: "turret",
  },
  plasmaGun: {
    id: "plasmaGun",
    name: "Plasma Gun",
    cost: 2.5, // MCr
    weight: 2, // tons
    power: 6,
    crew: 1,
    damage: "3D",
    range: "Medium",
    techLevel: 11,
    traits: [] as string[],
    description: "High-energy plasma weapon",
    mountType: "turret",
  },

  // Barbette Weapons
  beamLaserBarbette: {
    id: "beamLaserBarbette",
    name: "Beam Laser Barbette",
    cost: 3, // MCr
    weight: 5, // tons
    power: 12,
    crew: 1,
    damage: "2D",
    range: "Medium",
    techLevel: 10,
    traits: [] as string[],
    description: "Heavy beam laser barbette with DM+4 to attack rolls. Damage multiple ×3.",
    mountType: "barbette",
    attackModifier: 4,
    damageMultiple: 3,
  },
  fusionBarbette: {
    id: "fusionBarbette",
    name: "Fusion Barbette",
    cost: 4, // MCr
    weight: 5, // tons
    power: 20,
    crew: 1,
    damage: "5D",
    range: "Medium",
    techLevel: 12,
    traits: ["AP 3", "Radiation"] as string[],
    description: "Powerful fusion-based barbette weapon. Damage multiple ×3.",
    mountType: "barbette",
    damageMultiple: 3,
  },
  ionCannonBarbette: {
    id: "ionCannonBarbette",
    name: "Ion Cannon Barbette",
    cost: 6, // MCr
    weight: 5, // tons
    power: 10,
    crew: 1,
    damage: "7D",
    range: "Medium",
    techLevel: 12,
    traits: ["Ion"] as string[],
    description: "System-disrupting ion cannon barbette. Damage multiple ×3.",
    mountType: "barbette",
    damageMultiple: 3,
  },
  missileBarbette: {
    id: "missileBarbette",
    name: "Missile Barbette",
    cost: 4, // MCr
    weight: 5, // tons
    power: 0,
    crew: 1,
    damage: "4D",
    range: "Special",
    techLevel: 7,
    traits: ["Smart"] as string[],
    description: "Multilauncher missile barbette with 25 missiles (5 salvos × 5 missiles). No damage multiple (missiles exempt).",
    mountType: "barbette",
    ammo: {
      type: "missile",
      capacity: 25,
      tonsPerReload: 2,
      costPerReload: 520825, // 25 standard missiles at Cr20,833 each (Cr250,000 ÷ 12 × 25)
    },
  },
  particleBarbette: {
    id: "particleBarbette",
    name: "Particle Barbette",
    cost: 8, // MCr
    weight: 5, // tons
    power: 15,
    crew: 1,
    damage: "4D",
    range: "Very Long",
    techLevel: 11,
    traits: ["Radiation"] as string[],
    description: "Large particle weapon barbette. Damage multiple ×3.",
    mountType: "barbette",
    damageMultiple: 3,
  },
  plasmaBarbette: {
    id: "plasmaBarbette",
    name: "Plasma Barbette",
    cost: 5, // MCr
    weight: 5, // tons
    power: 12,
    crew: 1,
    damage: "4D",
    range: "Medium",
    techLevel: 11,
    traits: ["AP 2"] as string[],
    description: "Large plasma weapon barbette. Damage multiple ×3.",
    mountType: "barbette",
    damageMultiple: 3,
  },
  pulseLaserBarbette: {
    id: "pulseLaserBarbette",
    name: "Pulse Laser Barbette",
    cost: 6, // MCr
    weight: 5, // tons
    power: 12,
    crew: 1,
    damage: "3D",
    range: "Long",
    techLevel: 9,
    traits: [] as string[],
    description: "Heavy pulse laser barbette with DM+2 to attack rolls. Damage multiple ×3.",
    mountType: "barbette",
    attackModifier: 2,
    damageMultiple: 3,
  },
  railgunBarbette: {
    id: "railgunBarbette",
    name: "Railgun Barbette",
    cost: 2, // MCr
    weight: 5, // tons
    power: 5,
    crew: 1,
    damage: "3D",
    range: "Medium",
    techLevel: 10,
    traits: ["AP 5"] as string[],
    description: "Large railgun barbette with 12 rounds. Damage multiple ×3.",
    mountType: "barbette",
    damageMultiple: 3,
    ammo: {
      type: "railgunBarbetteAmmo",
      capacity: 12,
      tonsPerReload: 2,
      costPerReload: 10000,
    },
  },
  torpedoBarbette: {
    id: "torpedoBarbette",
    name: "Torpedo Barbette",
    cost: 3, // MCr
    weight: 5, // tons
    power: 2,
    crew: 1,
    damage: "6D",
    range: "Special",
    techLevel: 7,
    traits: ["Smart"] as string[],
    description: "Heavy torpedo barbette with 3 torpedoes. No damage multiple (torpedoes exempt).",
    mountType: "barbette",
    ammo: {
      type: "torpedo",
      capacity: 3,
      tonsPerReload: 1,
      costPerReload: 150000, // 3 standard torpedoes at Cr50,000 each
    },
  },

  // Small Bay Weapons
  fusionGunBaySmall: {
    id: "fusionGunBaySmall",
    name: "Fusion Gun Bay (Small)",
    cost: 8, // MCr
    weight: 50, // tons
    power: 50,
    crew: 1,
    damage: "6D",
    range: "Medium",
    techLevel: 12,
    traits: ["AP 6", "Radiation"] as string[],
    description: "Small fusion gun bay with damage multiple 10",
    mountType: "smallBay",
    damageMultiple: 10,
  },
  ionCannonBaySmall: {
    id: "ionCannonBaySmall",
    name: "Ion Cannon Bay (Small)",
    cost: 15, // MCr
    weight: 50, // tons
    power: 20,
    crew: 1,
    damage: "6D",
    range: "Medium",
    techLevel: 12,
    traits: ["Ion"] as string[],
    description: "Small ion cannon bay with damage multiple 10",
    mountType: "smallBay",
    damageMultiple: 10,
  },
  massDriverBaySmall: {
    id: "massDriverBaySmall",
    name: "Mass Driver Bay (Small)",
    cost: 40, // MCr
    weight: 50, // tons
    power: 15,
    crew: 1,
    damage: "3D",
    range: "Short",
    techLevel: 8,
    traits: ["Orbital Bombardment"] as string[],
    description: "Small mass driver bay with damage multiple 10",
    mountType: "smallBay",
    damageMultiple: 10,
    ammo: {
      type: "massDriverBaySmallAmmo",
      capacity: 6,
      tonsPerReload: 2,
      costPerReload: 20000,
    },
  },
  mesonGunBaySmall: {
    id: "mesonGunBaySmall",
    name: "Meson Gun Bay (Small)",
    cost: 50, // MCr
    weight: 50, // tons
    power: 20,
    crew: 1,
    damage: "5D",
    range: "Long",
    techLevel: 11,
    traits: ["AP ∞", "Radiation"] as string[],
    description: "Small meson gun bay with damage multiple 10",
    mountType: "smallBay",
    damageMultiple: 10,
  },
  missileBaySmall: {
    id: "missileBaySmall",
    name: "Missile Bay (Small)",
    cost: 12, // MCr
    weight: 50, // tons
    power: 5,
    crew: 1,
    damage: "4D",
    range: "Special",
    techLevel: 7,
    traits: ["Smart"] as string[],
    description: "Small missile bay with 144 missiles. No damage multiple (missiles exempt).",
    mountType: "smallBay",
    ammo: {
      type: "missile",
      capacity: 144,
      tonsPerReload: 12,
      costPerReload: 2500000, // 144 standard missiles at Cr20,833 each
    },
  },
  orbitalStrikeMassDriverBaySmall: {
    id: "orbitalStrikeMassDriverBaySmall",
    name: "Orbital Strike Mass Driver Bay (Small)",
    cost: 25, // MCr
    weight: 50, // tons
    power: 35,
    crew: 1,
    damage: "7D",
    range: "Short",
    techLevel: 10,
    traits: ["Orbital Strike"] as string[],
    description: "Small orbital strike mass driver bay",
    mountType: "smallBay",
    damageMultiple: 10,
  },
  orbitalStrikeMissileBaySmall: {
    id: "orbitalStrikeMissileBaySmall",
    name: "Orbital Strike Missile Bay (Small)",
    cost: 16, // MCr
    weight: 50, // tons
    power: 5,
    crew: 1,
    damage: "3D",
    range: "Medium",
    techLevel: 10,
    traits: ["Orbital Strike"] as string[],
    description: "Small orbital strike missile bay with unguided kinetic missiles (Thrust 6, 3D damage each)",
    mountType: "smallBay",
    damageMultiple: 10,
    ammo: {
      type: "orbitalStrikeMissile",
      capacity: 144,
      tonsPerReload: 12,
      costPerReload: 1800000, // 144 orbital strike missiles at Cr150,000 ÷ 12 = Cr12,500 each
    },
  },
  particleBeamBaySmall: {
    id: "particleBeamBaySmall",
    name: "Particle Beam Bay (Small)",
    cost: 20, // MCr
    weight: 50, // tons
    power: 30,
    crew: 1,
    damage: "6D",
    range: "Very Long",
    techLevel: 11,
    traits: ["Radiation"] as string[],
    description: "Small particle beam bay with damage multiple 10",
    mountType: "smallBay",
    damageMultiple: 10,
  },
  railgunBaySmall: {
    id: "railgunBaySmall",
    name: "Railgun Bay (Small)",
    cost: 30, // MCr
    weight: 50, // tons
    power: 10,
    crew: 1,
    damage: "3D",
    range: "Short",
    techLevel: 10,
    traits: ["AP 10"] as string[],
    description: "Small railgun bay with damage multiple 10",
    mountType: "smallBay",
    damageMultiple: 10,
    ammo: {
      type: "railgunBaySmallAmmo",
      capacity: 12,
      tonsPerReload: 1,
      costPerReload: 15000,
    },
  },
  repulsorBaySmall: {
    id: "repulsorBaySmall",
    name: "Repulsor Bay (Small)",
    cost: 30, // MCr
    weight: 50, // tons
    power: 50,
    crew: 1,
    damage: "Special",
    range: "Short",
    techLevel: 15,
    traits: [] as string[],
    description: "Small repulsor bay for missile defense and tractor beam. Can hold objects up to 100 tons. Removes 1D×Effect missiles from salvos within range.",
    mountType: "smallBay",
    damageMultiple: 10,
  },
  torpedoBaySmall: {
    id: "torpedoBaySmall",
    name: "Torpedo Bay (Small)",
    cost: 3, // MCr
    weight: 50, // tons
    power: 2,
    crew: 1,
    damage: "6D",
    range: "Special",
    techLevel: 7,
    traits: ["Smart"] as string[],
    description: "Small torpedo bay with 36 torpedoes. No damage multiple (torpedoes exempt).",
    mountType: "smallBay",
    ammo: {
      type: "torpedo",
      capacity: 36,
      tonsPerReload: 12,
      costPerReload: 1800000, // 36 standard torpedoes at Cr50,000 each
    },
  },

  // Medium Bay Weapons
  fusionGunBayMedium: {
    id: "fusionGunBayMedium",
    name: "Fusion Gun Bay (Medium)",
    cost: 14, // MCr
    weight: 100, // tons
    power: 80,
    crew: 2,
    damage: "7D",
    range: "Medium",
    techLevel: 12,
    traits: ["AP 6", "Radiation"] as string[],
    description: "Medium fusion gun bay with damage multiple 20",
    mountType: "mediumBay",
    damageMultiple: 20,
  },
  ionCannonBayMedium: {
    id: "ionCannonBayMedium",
    name: "Ion Cannon Bay (Medium)",
    cost: 25, // MCr
    weight: 100, // tons
    power: 30,
    crew: 2,
    damage: "8D",
    range: "Medium",
    techLevel: 12,
    traits: ["Ion"] as string[],
    description: "Medium ion cannon bay with damage multiple 20",
    mountType: "mediumBay",
    damageMultiple: 20,
  },
  massDriverBayMedium: {
    id: "massDriverBayMedium",
    name: "Mass Driver Bay (Medium)",
    cost: 60, // MCr
    weight: 100, // tons
    power: 25,
    crew: 2,
    damage: "4D",
    range: "Short",
    techLevel: 8,
    traits: ["Orbital Bombardment"] as string[],
    description: "Medium mass driver bay with damage multiple 20",
    mountType: "mediumBay",
    damageMultiple: 20,
    ammo: {
      type: "massDriverBayMediumAmmo",
      capacity: 6,
      tonsPerReload: 4,
      costPerReload: 40000,
    },
  },
  mesonGunBayMedium: {
    id: "mesonGunBayMedium",
    name: "Meson Gun Bay (Medium)",
    cost: 60, // MCr
    weight: 100, // tons
    power: 30,
    crew: 2,
    damage: "6D",
    range: "Long",
    techLevel: 12,
    traits: ["AP ∞", "Radiation"] as string[],
    description: "Medium meson gun bay with damage multiple 20",
    mountType: "mediumBay",
    damageMultiple: 20,
  },
  missileBayMedium: {
    id: "missileBayMedium",
    name: "Missile Bay (Medium)",
    cost: 20, // MCr
    weight: 100, // tons
    power: 10,
    crew: 2,
    damage: "4D",
    range: "Special",
    techLevel: 7,
    traits: ["Smart"] as string[],
    description: "Medium missile bay with 288 missiles. No damage multiple (missiles exempt).",
    mountType: "mediumBay",
    ammo: {
      type: "missile",
      capacity: 288,
      tonsPerReload: 24,
      costPerReload: 5000000, // 288 standard missiles at Cr20,833 each
    },
  },
  orbitalStrikeMassDriverBayMedium: {
    id: "orbitalStrikeMassDriverBayMedium",
    name: "Orbital Strike Mass Driver Bay (Medium)",
    cost: 35, // MCr
    weight: 100, // tons
    power: 50,
    crew: 2,
    damage: "10D",
    range: "Short",
    techLevel: 10,
    traits: ["Orbital Strike"] as string[],
    description: "Medium orbital strike mass driver bay",
    mountType: "mediumBay",
    damageMultiple: 20,
    ammo: {
      type: "orbitalStrikeMissile",
      capacity: 288,
      tonsPerReload: 24,
      costPerReload: 3600000, // 288 orbital strike missiles at Cr12,500 each
    },
  },
  orbitalStrikeMissileBayMedium: {
    id: "orbitalStrikeMissileBayMedium",
    name: "Orbital Strike Missile Bay (Medium)",
    cost: 20, // MCr
    weight: 100, // tons
    power: 15,
    crew: 2,
    damage: "5D",
    range: "Medium",
    techLevel: 10,
    traits: ["Orbital Strike"] as string[],
    description: "Medium orbital strike missile bay with unguided kinetic missiles (Thrust 6, 3D damage each)",
    mountType: "mediumBay",
    damageMultiple: 20,
    ammo: {
      type: "orbitalStrikeMissile",
      capacity: 288,
      tonsPerReload: 24,
      costPerReload: 3600000, // 288 orbital strike missiles at Cr12,500 each
    },
  },
  particleBeamBayMedium: {
    id: "particleBeamBayMedium",
    name: "Particle Beam Bay (Medium)",
    cost: 40, // MCr
    weight: 100, // tons
    power: 50,
    crew: 2,
    damage: "8D",
    range: "Very Long",
    techLevel: 12,
    traits: ["Radiation"] as string[],
    description: "Medium particle beam bay with damage multiple 20",
    mountType: "mediumBay",
    damageMultiple: 20,
  },
  railgunBayMedium: {
    id: "railgunBayMedium",
    name: "Railgun Bay (Medium)",
    cost: 50, // MCr
    weight: 100, // tons
    power: 15,
    crew: 2,
    damage: "5D",
    range: "Short",
    techLevel: 10,
    traits: ["AP 10"] as string[],
    description: "Medium railgun bay with damage multiple 20",
    mountType: "mediumBay",
    damageMultiple: 20,
    ammo: {
      type: "railgunBayMediumAmmo",
      capacity: 12,
      tonsPerReload: 2,
      costPerReload: 30000,
    },
  },
  repulsorBayMedium: {
    id: "repulsorBayMedium",
    name: "Repulsor Bay (Medium)",
    cost: 60, // MCr
    weight: 100, // tons
    power: 100,
    crew: 2,
    damage: "Special",
    range: "Short",
    techLevel: 14,
    traits: [] as string[],
    description: "Medium repulsor bay for missile defense and tractor beam. Can hold objects up to 200 tons. Removes 2D×Effect missiles from salvos within range.",
    mountType: "mediumBay",
    damageMultiple: 20,
  },
  torpedoBayMedium: {
    id: "torpedoBayMedium",
    name: "Torpedo Bay (Medium)",
    cost: 6, // MCr
    weight: 100, // tons
    power: 5,
    crew: 2,
    damage: "6D",
    range: "Special",
    techLevel: 7,
    traits: ["Smart"] as string[],
    description: "Medium torpedo bay with 72 torpedoes. No damage multiple (torpedoes exempt).",
    mountType: "mediumBay",
    ammo: {
      type: "torpedo",
      capacity: 72,
      tonsPerReload: 24,
      costPerReload: 3600000, // 72 standard torpedoes at Cr50,000 each
    },
  },

  // Large Bay Weapons
  fusionGunBayLarge: {
    id: "fusionGunBayLarge",
    name: "Fusion Gun Bay (Large)",
    cost: 25, // MCr
    weight: 500, // tons
    power: 100,
    crew: 4,
    damage: "10D",
    range: "Long",
    techLevel: 12,
    traits: ["AP 8", "Radiation"] as string[],
    description: "Large fusion gun bay with damage multiple 100",
    mountType: "largeBay",
    damageMultiple: 100,
  },
  ionCannonBayLarge: {
    id: "ionCannonBayLarge",
    name: "Ion Cannon Bay (Large)",
    cost: 40, // MCr
    weight: 500, // tons
    power: 40,
    crew: 4,
    damage: "10D",
    range: "Long",
    techLevel: 12,
    traits: ["Ion"] as string[],
    description: "Large ion cannon bay with damage multiple 100",
    mountType: "largeBay",
    damageMultiple: 100,
  },
  massDriverBayLarge: {
    id: "massDriverBayLarge",
    name: "Mass Driver Bay (Large)",
    cost: 80, // MCr
    weight: 500, // tons
    power: 35,
    crew: 4,
    damage: "6D",
    range: "Medium",
    techLevel: 8,
    traits: ["Orbital Bombardment"] as string[],
    description: "Large mass driver bay with damage multiple 100",
    mountType: "largeBay",
    damageMultiple: 100,
    ammo: {
      type: "massDriverBayLargeAmmo",
      capacity: 6,
      tonsPerReload: 20,
      costPerReload: 200000,
    },
  },
  mesonGunBayLarge: {
    id: "mesonGunBayLarge",
    name: "Meson Gun Bay (Large)",
    cost: 250, // MCr
    weight: 500, // tons
    power: 120,
    crew: 4,
    damage: "6D",
    range: "Long",
    techLevel: 13,
    traits: ["AP ∞", "Radiation"] as string[],
    description: "Large meson gun bay with damage multiple 100",
    mountType: "largeBay",
    damageMultiple: 100,
  },
  missileBayLarge: {
    id: "missileBayLarge",
    name: "Missile Bay (Large)",
    cost: 25, // MCr
    weight: 500, // tons
    power: 20,
    crew: 4,
    damage: "4D",
    range: "Special",
    techLevel: 7,
    traits: ["Smart"] as string[],
    description: "Large missile bay with 1,440 missiles. No damage multiple (missiles exempt).",
    mountType: "largeBay",
    ammo: {
      type: "missile",
      capacity: 1440,
      tonsPerReload: 120,
      costPerReload: 25000000, // 1,440 standard missiles at Cr20,833 each
    },
  },
  orbitalStrikeMassDriverBayLarge: {
    id: "orbitalStrikeMassDriverBayLarge",
    name: "Orbital Strike Mass Driver Bay (Large)",
    cost: 50, // MCr
    weight: 500, // tons
    power: 75,
    crew: 4,
    damage: "12D",
    range: "Short",
    techLevel: 10,
    traits: ["Orbital Strike"] as string[],
    description: "Large orbital strike mass driver bay",
    mountType: "largeBay",
    damageMultiple: 100,
  },
  orbitalStrikeMissileBayLarge: {
    id: "orbitalStrikeMissileBayLarge",
    name: "Orbital Strike Missile Bay (Large)",
    cost: 24, // MCr
    weight: 500, // tons
    power: 25,
    crew: 4,
    damage: "8D",
    range: "Medium",
    techLevel: 10,
    traits: ["Orbital Strike"] as string[],
    description: "Large orbital strike missile bay with unguided kinetic missiles (Thrust 6, 3D damage each)",
    mountType: "largeBay",
    damageMultiple: 100,
    ammo: {
      type: "orbitalStrikeMissile",
      capacity: 1440,
      tonsPerReload: 120,
      costPerReload: 18000000, // 1,440 orbital strike missiles at Cr12,500 each
    },
  },
  particleBeamBayLarge: {
    id: "particleBeamBayLarge",
    name: "Particle Beam Bay (Large)",
    cost: 60, // MCr
    weight: 500, // tons
    power: 80,
    crew: 4,
    damage: "10D",
    range: "Distant",
    techLevel: 13,
    traits: ["Radiation"] as string[],
    description: "Large particle beam bay with damage multiple 100",
    mountType: "largeBay",
    damageMultiple: 100,
  },
  railgunBayLarge: {
    id: "railgunBayLarge",
    name: "Railgun Bay (Large)",
    cost: 70, // MCr
    weight: 500, // tons
    power: 25,
    crew: 4,
    damage: "6D",
    range: "Medium",
    techLevel: 10,
    traits: ["AP 10"] as string[],
    description: "Large railgun bay with damage multiple 100",
    mountType: "largeBay",
    damageMultiple: 100,
    ammo: {
      type: "railgunBayLargeAmmo",
      capacity: 12,
      tonsPerReload: 5,
      costPerReload: 75000,
    },
  },
  repulsorBayLarge: {
    id: "repulsorBayLarge",
    name: "Repulsor Bay (Large)",
    cost: 90, // MCr
    weight: 500, // tons
    power: 200,
    crew: 4,
    damage: "Special",
    range: "Short",
    techLevel: 13,
    traits: [] as string[],
    description: "Large repulsor bay for missile defense and tractor beam. Can hold objects up to 800 tons. Removes 5D×Effect missiles from salvos within range.",
    mountType: "largeBay",
    damageMultiple: 100,
  },
  torpedoBayLarge: {
    id: "torpedoBayLarge",
    name: "Torpedo Bay (Large)",
    cost: 10, // MCr
    weight: 500, // tons
    power: 10,
    crew: 4,
    damage: "6D",
    range: "Special",
    techLevel: 7,
    traits: ["Smart"] as string[],
    description: "Large torpedo bay with 360 torpedoes. No damage multiple (torpedoes exempt).",
    mountType: "largeBay",
    ammo: {
      type: "torpedo",
      capacity: 360,
      tonsPerReload: 120,
      costPerReload: 18000000, // 360 standard torpedoes at Cr50,000 each
    },
  },

  // Spinal Weapons (Base Size)
  massDriverSpinal: {
    id: "massDriverSpinal",
    name: "Mass Driver Spinal Mount",
    cost: 1500, // MCr
    weight: 5000, // tons (base size)
    power: 250,
    crew: 1,
    damage: "4D",
    range: "Short",
    techLevel: 10,
    traits: ["AP 15", "Orbital Bombardment"] as string[],
    description: "Used against precise targets like infrastructure, industrial centres, and defensive installations. Creates multiple shockwaves that converge on target. Ammunition: 50 tons, Cr500,000 per attack.",
    mountType: "spinalMount",
    damageMultiple: 1000,
    baseSize: 5000,
    maxSize: 100000,
    ammo: {
      type: "massDriverSpinalAmmo",
      capacity: 1,
      tonsPerReload: 50,
      costPerReload: 500000,
    },
  },
  mesonSpinal: {
    id: "mesonSpinal",
    name: "Meson Spinal Mount",
    cost: 2000, // MCr
    weight: 7500, // tons (base size)
    power: 1000,
    crew: 1,
    damage: "6D",
    range: "Long",
    techLevel: 12,
    traits: ["AP ∞", "Radiation"] as string[],
    description: "A truly awe-inspiring weapon using dual-particle beams that can end battles before they begin. Ignores all armour and radiation shielding.",
    mountType: "spinalMount",
    damageMultiple: 1000,
    baseSize: 7500,
    maxSize: 75000,
  },
  particleSpinal: {
    id: "particleSpinal",
    name: "Particle Spinal Mount",
    cost: 1000, // MCr
    weight: 3500, // tons (base size)
    power: 1000,
    crew: 1,
    damage: "8D",
    range: "Very Long",
    techLevel: 11,
    traits: ["Radiation", "Armour Reduction 3%"] as string[],
    description: "The largest version of particle weapon systems, a mainstay of powerful navies. Damage is reduced by 3% per point of armour possessed by the target before applying the Damage Multiple.",
    mountType: "spinalMount",
    damageMultiple: 1000,
    baseSize: 3500,
    maxSize: 28000,
  },
  railgunSpinal: {
    id: "railgunSpinal",
    name: "Railgun Spinal Mount",
    cost: 500, // MCr
    weight: 3500, // tons (base size)
    power: 500,
    crew: 1,
    damage: "4D",
    range: "Medium",
    techLevel: 10,
    traits: ["AP 20", "Armour Reduction 2%"] as string[],
    description: "An array of smaller rapid-firing guns firing large rounds at near relativistic speeds. Damage is reduced by 2% per point of armour possessed by the target before applying the Damage Multiple. Comes with 5 rounds. Extra rounds: 20 tons, MCr0.2 each.",
    mountType: "spinalMount",
    damageMultiple: 1000,
    baseSize: 3500,
    maxSize: 21000,
    ammo: {
      type: "railgunSpinalAmmo",
      capacity: 5,
      tonsPerReload: 20,
      costPerReload: 200000,
    },
  },

  // Point-Defense Laser Batteries
  pointDefenseLaserI: {
    id: "pointDefenseLaserI",
    name: "Point-Defense Laser Battery I",
    cost: 5, // MCr
    weight: 20, // tons
    power: 10,
    crew: 0,
    damage: "Special",
    range: "Close",
    techLevel: 10,
    traits: ["Point Defense"] as string[],
    description: "+2D Intercept, no gunner required",
    mountType: "pointDefense",
    intercept: 2,
  },
  pointDefenseLaserII: {
    id: "pointDefenseLaserII",
    name: "Point-Defense Laser Battery II",
    cost: 10, // MCr
    weight: 20, // tons
    power: 20,
    crew: 0,
    damage: "Special",
    range: "Close",
    techLevel: 12,
    traits: ["Point Defense"] as string[],
    description: "+4D Intercept, no gunner required",
    mountType: "pointDefense",
    intercept: 4,
  },
  pointDefenseLaserIII: {
    id: "pointDefenseLaserIII",
    name: "Point-Defense Laser Battery III",
    cost: 20, // MCr
    weight: 20, // tons
    power: 30,
    crew: 0,
    damage: "Special",
    range: "Close",
    techLevel: 14,
    traits: ["Point Defense"] as string[],
    description: "+6D Intercept, no gunner required",
    mountType: "pointDefense",
    intercept: 6,
  },
  // Point-Defense Gauss Batteries
  pointDefenseGaussI: {
    id: "pointDefenseGaussI",
    name: "Point-Defense Gauss Battery I",
    cost: 3, // MCr
    weight: 20, // tons
    power: 5,
    crew: 0,
    damage: "Special",
    range: "Close",
    techLevel: 10,
    traits: ["Point Defense", "Ammunition"] as string[],
    description:
      "+2D Intercept, ammo required, less power, DM-2 vs Thrust 12-14, DM-6 vs Thrust 15+",
    mountType: "pointDefense",
    intercept: 2,
    ammo: {
      type: "gaussAmmo",
      capacity: 12,
      tonsPerReload: 1,
      costPerReload: 30000, // Cr30,000 per canister
    },
  },
  pointDefenseGaussII: {
    id: "pointDefenseGaussII",
    name: "Point-Defense Gauss Battery II",
    cost: 6, // MCr
    weight: 20, // tons
    power: 15,
    crew: 0,
    damage: "Special",
    range: "Close",
    techLevel: 12,
    traits: ["Point Defense", "Ammunition"] as string[],
    description:
      "+4D Intercept, ammo required, less power, DM-2 vs Thrust 12-14, DM-6 vs Thrust 15+",
    mountType: "pointDefense",
    intercept: 4,
    ammo: {
      type: "gaussAmmo",
      capacity: 12,
      tonsPerReload: 1,
      costPerReload: 30000, // Cr30,000 per canister
    },
  },
  pointDefenseGaussIII: {
    id: "pointDefenseGaussIII",
    name: "Point-Defense Gauss Battery III",
    cost: 10, // MCr
    weight: 20, // tons
    power: 25,
    crew: 0,
    damage: "Special",
    range: "Close",
    techLevel: 14,
    traits: ["Point Defense", "Ammunition"] as string[],
    description:
      "+6D Intercept, ammo required, less power, DM-2 vs Thrust 12-14, DM-6 vs Thrust 15+",
    mountType: "pointDefense",
    intercept: 6,
    ammo: {
      type: "gaussAmmo",
      capacity: 12,
      tonsPerReload: 1,
      costPerReload: 30000, // Cr30,000 per canister
    },
  },
} as const;

// Spinal Weapon Range Adjustments
// According to Traveller rules, spinal weapons incur negative DMs that vary by both range and attacking ship size
export const SPINAL_WEAPON_RANGE_ADJUSTMENTS = {
  // Range adjustments by attacking ship size
  // Format: [<10k tons, 10k-50k tons, 50k-250k tons, >250k tons]
  Adjacent: [-4, -6, -8, -10],
  Close: [-2, -4, -6, -8],
  Short: [-1, -2, -4, -6],
  Medium: [0, 0, 0, 0],
  Long: [0, 0, 0, 0],
  "Very Long": [0, 0, 0, 0],
  Distant: [0, 0, 0, 0],
} as const;

// Spinal Weapon Size Penalties
export const SPINAL_WEAPON_SIZE_PENALTIES = {
  Tiny: -4,
  Small: -2,
  Medium: 0,
  Large: 2,
  Huge: 4,
} as const;

// Spinal Weapons
export const SPINAL_WEAPONS = {
  PARTICLE_BEAM: {
    name: "Particle Beam",
    description: "High-powered particle beam weapon",
    baseCost: 50000000,
    baseTons: 50,
    basePower: 100,
    techLevel: 12,
    damage: "3D",
    range: "Long",
    special: "Ignores armor",
  },
  MESON_GUN: {
    name: "Meson Gun",
    description: "Advanced meson-based weapon",
    baseCost: 100000000,
    baseTons: 100,
    basePower: 200,
    techLevel: 15,
    damage: "4D",
    range: "Very Long",
    special: "Ignores armor and screens",
  },
} as const;

// Missile Weapons
export const MISSILE_WEAPONS = {
  STANDARD: {
    name: "Standard Missile",
    description: "Basic missile system",
    baseCost: 1000000,
    baseTons: 1,
    basePower: 1,
    techLevel: 7,
    damage: "2D",
    range: "Medium",
    ammo: {
      missilesPerTon: 100,
      costPerMissile: 1000,
    },
  },
} as const;

// Sandcaster Weapons
export const SANDCASTER_WEAPONS = {
  STANDARD: {
    name: "Standard Sandcaster",
    description: "Basic sandcaster system",
    baseCost: 500000,
    baseTons: 1,
    basePower: 1,
    techLevel: 7,
    damage: "1D",
    range: "Close",
    ammo: {
      canistersPerTon: 200,
      costPerCanister: 500,
    },
  },
} as const;

// Torpedo Weapons
export const TORPEDO_WEAPONS = {
  STANDARD: {
    name: "Standard Torpedo",
    description: "Basic torpedo system",
    baseCost: 2000000,
    baseTons: 2,
    basePower: 2,
    techLevel: 8,
    damage: "3D",
    range: "Long",
    ammo: {
      torpedoesPerTon: 50,
      costPerTorpedo: 2000,
    },
  },
} as const;

// Point Defense Weapons
export const POINT_DEFENSE_WEAPONS = {
  STANDARD: {
    name: "Standard Point Defense",
    description: "Basic point defense system",
    baseCost: 1000000,
    baseTons: 1,
    basePower: 1,
    techLevel: 7,
    damage: "1D",
    range: "Close",
    ammo: {
      roundsPerTon: 1000,
      costPerRound: 100,
    },
  },
} as const;

// Screen Weapons
export const SCREEN_WEAPONS = {
  STANDARD: {
    name: "Standard Screen",
    description: "Basic screen system",
    baseCost: 5000000,
    baseTons: 5,
    basePower: 5,
    techLevel: 8,
    damage: "N/A",
    range: "N/A",
    special: "Reduces incoming damage by 1D",
    damageReduction: "1D",
    radiationRemoval: false,
    destructiveWeaponReduction: null,
  },
  NUCLEAR_DAMPER: {
    name: "Nuclear Damper",
    description:
      "Advanced screen system that reduces damage from nuclear weapons",
    baseCost: 10000000,
    baseTons: 10,
    basePower: 10,
    techLevel: 12,
    damage: "N/A",
    range: "N/A",
    special:
      "Reduces incoming damage by 2D, reduces nuclear weapon damage by 3DD",
    damageReduction: "2D",
    radiationRemoval: true,
    destructiveWeaponReduction: 3,
  },
} as const;

// Defensive Systems (Screens and Black Globe)
export const DEFENSIVE_SYSTEMS = {
  mesonScreen: {
    id: "mesonScreen",
    name: "Meson Screen",
    cost: 20, // MCr
    weight: 10, // tons
    power: 30,
    crew: 1,
    techLevel: 13,
    traits: ["Screen", "Meson"] as string[],
    description:
      "Reduces meson weapon damage by 2D x 10 and removes Radiation trait",
    effect: "2D x 10 damage reduction vs meson weapons",
  },
  nuclearDamper: {
    id: "nuclearDamper",
    name: "Nuclear Damper",
    cost: 10, // MCr
    weight: 10, // tons
    power: 20,
    crew: 1,
    techLevel: 12,
    traits: ["Screen", "Nuclear"] as string[],
    description:
      "Reduces fusion/nuclear weapon damage by 2D and removes Radiation trait",
    effect: "2D damage reduction vs fusion/nuclear weapons",
  },
  blackGlobeGenerator: {
    id: "blackGlobeGenerator",
    name: "Black Globe Generator",
    cost: 100, // MCr
    weight: 50, // tons
    power: 30,
    crew: 1,
    techLevel: 15,
    traits: ["Screen", "Black Globe", "Experimental"] as string[],
    description:
      "Absorbs all incoming attacks, requires capacitors, operational limitations",
    effect: "Absorbs all attacks, requires capacitor management",
    flickerRates: [
      { rate: 1, attackThreshold: 6, thrustPenalty: -1, sensorPenalty: -1 },
      { rate: 2, attackThreshold: 5, thrustPenalty: -2, sensorPenalty: -2 },
      { rate: 3, attackThreshold: 4, thrustPenalty: -3, sensorPenalty: -3 },
      { rate: 4, attackThreshold: 3, thrustPenalty: -4, sensorPenalty: -4 },
      { rate: 5, attackThreshold: 2, thrustPenalty: -5, sensorPenalty: -5 },
      { rate: 6, attackThreshold: 0, thrustPenalty: 0, sensorPenalty: 0 }, // Always on
    ],
    capacitorAbsorption: 50, // damage per ton of capacitors
    capacitorCost: 3, // MCr per ton of additional capacitors
  },
} as const;

export const CREW_TYPES: Record<CrewType, CrewTypeDefinition> = {
  bridge: {
    name: "Bridge Crew",
    description: "Required for ship operation and navigation",
    required: (
      shipSize: number,
      hasJumpDrive: boolean,
      smallCraftCount: number,
      weaponCounts: Record<string, number>,
      passengerCounts: Record<string, number>,
    ) => 1 + smallCraftCount,
    baseRequired: (shipSize: number) => 1,
  },
  engineering: {
    name: "Engineering Crew",
    description: "Required for drive and power plant operation",
    required: (
      shipSize: number,
      hasJumpDrive: boolean,
      smallCraftCount: number,
      weaponCounts: Record<string, number>,
      passengerCounts: Record<string, number>,
    ) => {
      return Math.ceil(shipSize / 35);
    },
    baseRequired: (shipSize: number) => Math.ceil(shipSize / 35),
  },
  gunnery: {
    name: "Gunnery Crew",
    description: "Required for weapon operation",
    required: (
      shipSize: number,
      hasJumpDrive: boolean,
      smallCraftCount: number,
      weaponCounts: Record<string, number>,
      passengerCounts: Record<string, number>,
    ) => {
      const totalWeapons = Object.values(weaponCounts || {}).reduce(
        (a: number, b: number) => a + b,
        0,
      );
      return totalWeapons;
    },
    baseRequired: (shipSize: number) => 0,
    weaponsRequired: 1,
  },
  medical: {
    name: "Medical Crew",
    description: "Required for medical care",
    required: (
      shipSize: number,
      hasJumpDrive: boolean,
      smallCraftCount: number,
      weaponCounts: Record<string, number>,
      passengerCounts: Record<string, number>,
    ) => {
      const totalCrew = Object.values(weaponCounts || {}).reduce(
        (a: number, b: number) => a + b,
        0,
      );
      const totalPassengers = Object.values(passengerCounts || {}).reduce(
        (a: number, b: number) => a + b,
        0,
      );
      return Math.ceil((totalCrew + totalPassengers) / 120);
    },
    baseRequired: (shipSize: number) => 0,
    passengersRequired: 1 / 120,
  },
  service: {
    name: "Service Crew",
    description: "Required for passenger service and ship administration",
    required: (
      shipSize: number,
      hasJumpDrive: boolean,
      smallCraftCount: number,
      weaponCounts: Record<string, number>,
      passengerCounts: Record<string, number>,
    ) => {
      const totalPassengers = Object.values(passengerCounts || {}).reduce(
        (a: number, b: number) => a + b,
        0,
      );
      return Math.ceil(totalPassengers / 10) + Math.ceil(shipSize / 2000);
    },
    baseRequired: (shipSize: number) => Math.ceil(shipSize / 2000),
    passengersRequired: 0.1,
  },
};

export const SHIP_SIZES = {
  tiny: {
    name: "Tiny",
    minSize: 1,
    maxSize: 50,
    description: "Small craft and fighters",
  },
  small: {
    name: "Small",
    minSize: 51,
    maxSize: 200,
    description: "Small ships and yachts",
  },
  medium: {
    name: "Medium",
    minSize: 201,
    maxSize: 1000,
    description: "Standard ships and freighters",
  },
  large: {
    name: "Large",
    minSize: 1001,
    maxSize: 5000,
    description: "Large ships and liners",
  },
  huge: {
    name: "Huge",
    minSize: 5001,
    maxSize: 100000,
    description: "Capital ships and megafreighters",
  },
} as const;

export const TECH_LEVELS = {
  7: {
    name: "Early Space Age",
    description: "Basic space travel, primitive computers",
  },
  8: {
    name: "Early Interstellar",
    description: "Jump drive, advanced computers",
  },
  9: {
    name: "Early Interstellar",
    description: "Improved jump drive, better computers",
  },
  10: {
    name: "Average Interstellar",
    description: "Standard jump drive, good computers",
  },
  11: {
    name: "Average Interstellar",
    description: "Improved jump drive, advanced computers",
  },
  12: {
    name: "High Interstellar",
    description: "Advanced jump drive, sophisticated computers",
  },
  13: {
    name: "High Interstellar",
    description: "Very advanced jump drive, sophisticated computers",
  },
  14: {
    name: "Very High Interstellar",
    description: "Cutting-edge jump drive, advanced AI",
  },
  15: {
    name: "Very High Interstellar",
    description: "Experimental jump drive, advanced AI",
  },
  16: {
    name: "Ultra High Interstellar",
    description: "Revolutionary jump drive, sentient AI",
  },
  17: {
    name: "Ultra High Interstellar",
    description: "Revolutionary jump drive, sentient AI",
  },
  18: {
    name: "Ultra High Interstellar",
    description: "Revolutionary jump drive, sentient AI",
  },
  19: {
    name: "Ultra High Interstellar",
    description: "Revolutionary jump drive, sentient AI",
  },
  20: {
    name: "Ultra High Interstellar",
    description: "Revolutionary jump drive, sentient AI",
  },
} as const;

// Traveller Ammo Types
export const AMMO_TYPES = {
  basicMissile: {
    name: "Standard Missile",
    type: "missile",
    tl: 7,
    damage: "4D",
    cost: 250000, // Cost for 12 missiles
    tonsPerReload: 1, // 12 missiles = 1 ton
    traits: ["Smart"],
    perReload: 12,
  },
  advancedMissile: {
    name: "Advanced Missile",
    type: "missile",
    tl: 14,
    damage: "5D",
    cost: 350000,
    tonsPerReload: 1,
    traits: ["Smart"],
    perReload: 12,
  },
  nuclearMissile: {
    name: "Nuclear Missile",
    type: "missile",
    tl: 6,
    damage: "1DD",
    cost: 450000,
    tonsPerReload: 1,
    traits: ["Radiation", "Smart"],
    perReload: 12,
  },
  fragmentationMissile: {
    name: "Fragmentation Missile",
    type: "missile",
    tl: 8,
    damage: "3D",
    cost: 200000,
    tonsPerReload: 1,
    traits: ["Smart"],
    perReload: 12,
  },
  decoyMissile: {
    name: "Decoy Missile",
    type: "missile",
    tl: 9,
    damage: "2D",
    cost: 150000,
    tonsPerReload: 1,
    traits: ["Smart"],
    perReload: 12,
  },
  antiTorpedoMissile: {
    name: "Anti-Torpedo Missile",
    type: "missile",
    tl: 13,
    damage: "1D*",
    cost: 350000,
    tonsPerReload: 1,
    traits: ["Smart"],
    perReload: 12,
  },
  ionMissile: {
    name: "Ion Missile",
    type: "missile",
    tl: 12,
    damage: "Special",
    cost: 750000,
    tonsPerReload: 1,
    traits: ["Ion"],
    perReload: 12,
  },
  jumpbreakerMissile: {
    name: "Jumpbreaker Missile",
    type: "missile",
    tl: 13,
    damage: "—",
    cost: 1000000,
    tonsPerReload: 1,
    traits: ["Smart"],
    perReload: 12,
  },
  longRangeMissile: {
    name: "Long Range Missile",
    type: "missile",
    tl: 8,
    damage: "3D",
    cost: 500000,
    tonsPerReload: 1,
    traits: ["Smart"],
    perReload: 12,
  },
  multiWarheadMissile: {
    name: "Multi-Warhead Missile",
    type: "missile",
    tl: 8,
    damage: "3D",
    cost: 750000,
    tonsPerReload: 1,
    traits: ["Smart"],
    perReload: 12,
  },
  antimatterMissile: {
    name: "Antimatter Missile",
    type: "missile",
    tl: 20,
    damage: "2DD",
    cost: 1000000,
    tonsPerReload: 1,
    traits: ["Radiation", "Smart"],
    perReload: 12,
  },
  ortilleryMissile: {
    name: "Ortillery Missile",
    type: "missile",
    tl: 7,
    damage: "1DD",
    cost: 300000,
    tonsPerReload: 1,
    traits: ["Orbital Strike"],
    perReload: 12,
  },
  shockwaveMissile: {
    name: "Shockwave Missile",
    type: "missile",
    tl: 7,
    damage: "—",
    cost: 200000,
    tonsPerReload: 1,
    traits: ["Smart"],
    perReload: 12,
  },
  orbitalStrikeMissile: {
    name: "Orbital Strike Kinetic Missile",
    type: "orbitalStrikeMissile",
    tl: 10,
    damage: "3D",
    cost: 150000, // Cost for 12 missiles
    tonsPerReload: 1, // 12 missiles = 1 ton
    traits: ["Orbital Strike"],
    perReload: 12,
  },
  basicTorpedo: {
    name: "Basic Torpedo",
    type: "torpedo",
    tl: 10,
    damage: "6d6",
    cost: 5000,
    tonsPerReload: 0.1,
    traits: ["Guided"],
    perReload: 6,
  },
  advancedTorpedoBarbette: {
    name: "Advanced Torpedo (Barbette)",
    type: "torpedo",
    tl: 14,
    damage: "7D",
    cost: 450000, // Cost for 3 torpedoes
    tonsPerReload: 1, // 3 torpedoes = 1 ton
    traits: ["Smart"],
    perReload: 3,
  },
  advancedTorpedo: {
    name: "Advanced Torpedo",
    type: "torpedo",
    tl: 12,
    damage: "8d6",
    cost: 15000,
    tonsPerReload: 0.1,
    traits: ["Guided", "Advanced"],
    perReload: 6,
  },
  basicSand: {
    name: "Basic Sand Canister",
    type: "sand",
    tl: 7,
    damage: "—",
    cost: 500,
    tonsPerReload: 0.05,
    traits: ["Point Defence"],
    perReload: 12,
  },
  advancedSand: {
    name: "Advanced Sand Canister",
    type: "sand",
    tl: 10,
    damage: "—",
    cost: 1500,
    tonsPerReload: 0.05,
    traits: ["Point Defence", "Advanced"],
    perReload: 12,
  },
  railgunBarbetteAmmo: {
    name: "Railgun Barbette Ammunition",
    type: "railgunBarbetteAmmo",
    tl: 10,
    damage: "—",
    cost: 10000, // Cost per reload (12 shots)
    tonsPerReload: 2, // 2 tons per reload (12 shots)
    traits: [],
    perReload: 12, // 12 attacks per reload
  },
  // Sand/Canister types
  sandCanister: {
    name: "Sand Canister",
    type: "sand",
    tl: 7,
    damage: "—",
    cost: 25000, // Cost for 20 canisters
    tonsPerReload: 1, // 20 canisters = 1 ton
    traits: [],
    perReload: 20,
  },
  antiPersonnelCanister: {
    name: "Anti-Personnel Canister",
    type: "sand",
    tl: 8,
    damage: "—",
    cost: 40000,
    tonsPerReload: 1,
    traits: [],
    perReload: 20,
  },
  chaffCanister: {
    name: "Chaff Canister",
    type: "sand",
    tl: 8,
    damage: "—",
    cost: 30000,
    tonsPerReload: 1,
    traits: [],
    perReload: 20,
  },
  pebbleCanister: {
    name: "Pebble Canister",
    type: "sand",
    tl: 7,
    damage: "—",
    cost: 25000,
    tonsPerReload: 1,
    traits: [],
    perReload: 20,
  },
  sandcutterCanister: {
    name: "Sandcutter Canister",
    type: "sand",
    tl: 8,
    damage: "—",
    cost: 35000,
    tonsPerReload: 1,
    traits: [],
    perReload: 20,
  },
  // Torpedo types
  standardTorpedo: {
    name: "Standard Torpedo",
    type: "torpedo",
    tl: 7,
    damage: "6D",
    cost: 150000, // Cost for 3 torpedoes
    tonsPerReload: 1, // 3 torpedoes = 1 ton
    traits: ["Smart"],
    perReload: 3,
  },
  antimatterTorpedo: {
    name: "Antimatter Torpedo",
    type: "torpedo",
    tl: 20,
    damage: "3DD",
    cost: 900000,
    tonsPerReload: 1,
    traits: ["Radiation", "Smart"],
    perReload: 3,
  },
  antimatterBombPumpedTorpedo: {
    name: "Antimatter Bomb-Pumped Torpedo",
    type: "torpedo",
    tl: 21,
    damage: "8D",
    cost: 800000,
    tonsPerReload: 1,
    traits: ["AP 10", "Radiation", "Smart"],
    perReload: 3,
  },
  antiradiationTorpedo: {
    name: "Antiradiation Torpedo",
    type: "torpedo",
    tl: 12,
    damage: "6D",
    cost: 300000,
    tonsPerReload: 1,
    traits: ["Smart"],
    perReload: 3,
  },
  bombPumpedTorpedo: {
    name: "Bomb-Pumped Torpedo",
    type: "torpedo",
    tl: 9,
    damage: "4D",
    cost: 250000,
    tonsPerReload: 1,
    traits: ["Smart"],
    perReload: 3,
  },
  ionTorpedo: {
    name: "Ion Torpedo",
    type: "torpedo",
    tl: 9,
    damage: "Special",
    cost: 230000,
    tonsPerReload: 1,
    traits: ["Smart"],
    perReload: 3,
  },
  multiWarheadAntimatterTorpedo: {
    name: "Multi-Warhead Antimatter Torpedo",
    type: "torpedo",
    tl: 21,
    damage: "1DD",
    cost: 2000000,
    tonsPerReload: 1,
    traits: ["Radiation", "Smart"],
    perReload: 3,
  },
  multiWarheadStandardTorpedo: {
    name: "Multi-Warhead Standard Torpedo",
    type: "torpedo",
    tl: 8,
    damage: "4D",
    cost: 400000,
    tonsPerReload: 1,
    traits: ["Smart"],
    perReload: 3,
  },
  multiWarheadNuclearTorpedo: {
    name: "Multi-Warhead Nuclear Torpedo",
    type: "torpedo",
    tl: 8,
    damage: "6D",
    cost: 600000,
    tonsPerReload: 1,
    traits: ["Radiation", "Smart"],
    perReload: 3,
  },
  nuclearTorpedo: {
    name: "Nuclear Torpedo",
    type: "torpedo",
    tl: 7,
    damage: "2DD",
    cost: 225000,
    tonsPerReload: 1,
    traits: ["Radiation", "Smart"],
    perReload: 3,
  },
  ortilleryTorpedo: {
    name: "Ortillery Torpedo",
    type: "torpedo",
    tl: 8,
    damage: "3DD",
    cost: 1000000,
    tonsPerReload: 1,
    traits: ["Orbital Strike"],
    perReload: 3,
  },
  plasmaTorpedo: {
    name: "Plasma Torpedo",
    type: "torpedo",
    tl: 12,
    damage: "1DD",
    cost: 650000,
    tonsPerReload: 1,
    traits: ["AP 10", "Smart"],
    perReload: 3,
  },
  railgunAmmo: {
    name: "Railgun Ammunition",
    type: "railgunAmmo",
    tl: 10,
    damage: "—",
    cost: 5000,
    tonsPerReload: 1,
    traits: [],
    perReload: 12,
  },
  railgunSpinalAmmo: {
    name: "Railgun Spinal Ammunition",
    type: "railgunSpinalAmmo",
    tl: 10,
    damage: "—",
    cost: 200000, // Cost per reload (5 shots = MCr0.2)
    tonsPerReload: 20, // 20 tons per reload (5 shots)
    traits: [],
    perReload: 5, // 5 attacks per reload
  },
  massDriverSpinalAmmo: {
    name: "Mass Driver Spinal Ammunition",
    type: "massDriverSpinalAmmo",
    tl: 10,
    damage: "—",
    cost: 500000, // Cost per reload (1 shot)
    tonsPerReload: 50, // 50 tons per reload (1 shot)
    traits: [],
    perReload: 1, // 1 attack per reload
  },
  massDriverBaySmallAmmo: {
    name: "Mass Driver Bay Ammunition (Small)",
    type: "massDriverBaySmallAmmo",
    tl: 8,
    damage: "—",
    cost: 20000, // Cost per attack
    tonsPerReload: 2, // Tons per attack
    traits: [],
    perReload: 6, // 6 attacks per reload
  },
  massDriverBayMediumAmmo: {
    name: "Mass Driver Bay Ammunition (Medium)",
    type: "massDriverBayMediumAmmo",
    tl: 8,
    damage: "—",
    cost: 40000, // Cost per attack
    tonsPerReload: 4, // Tons per attack
    traits: [],
    perReload: 6, // 6 attacks per reload
  },
  massDriverBayLargeAmmo: {
    name: "Mass Driver Bay Ammunition (Large)",
    type: "massDriverBayLargeAmmo",
    tl: 8,
    damage: "—",
    cost: 200000, // Cost per attack
    tonsPerReload: 20, // Tons per attack
    traits: [],
    perReload: 6, // 6 attacks per reload
  },
  railgunBaySmallAmmo: {
    name: "Railgun Bay Ammunition (Small)",
    type: "railgunBaySmallAmmo",
    tl: 10,
    damage: "—",
    cost: 15000, // Cost per attack
    tonsPerReload: 1, // Tons per attack
    traits: [],
    perReload: 12, // 12 attacks per reload
  },
  railgunBayMediumAmmo: {
    name: "Railgun Bay Ammunition (Medium)",
    type: "railgunBayMediumAmmo",
    tl: 10,
    damage: "—",
    cost: 30000, // Cost per attack
    tonsPerReload: 2, // Tons per attack
    traits: [],
    perReload: 12, // 12 attacks per reload
  },
  railgunBayLargeAmmo: {
    name: "Railgun Bay Ammunition (Large)",
    type: "railgunBayLargeAmmo",
    tl: 10,
    damage: "—",
    cost: 75000, // Cost per attack
    tonsPerReload: 5, // Tons per attack
    traits: [],
    perReload: 12, // 12 attacks per reload
  },
} as const;

const shipConstants = {
  POWER_PLANT_TYPES,
  STATEROOM_CONFIG,
  STATEROOM_TYPES,
  CREW_COSTS,
  MAINTENANCE_COSTS,
  BASE_COSTS,
  BASE_POWER,
  VALIDATION,
  ARMOR_TYPES,
};

export default shipConstants;
